import React from "react";

import type {
  CellColor,
  PivotCell as PivotCellData,
  PivotColumn,
  PivotRow,
  RowGroupLevel,
  RowGroupPath,
  ValidationPipeline,
} from "./stepBreakdown";
import type { StepFilterClick } from "./stepFilterTokenToggle";

export interface PivotCellProps {
  readonly cell: PivotCellData;
  readonly row: PivotRow;
  readonly column: PivotColumn;
  readonly onClick: (click: StepFilterClick) => void;
  readonly isActiveFilter: boolean;
}

const TEXT_COLOR_BY_COLOR: Record<CellColor, string> = {
  green: "var(--color-text-status-success, #037f0c)",
  orange: "var(--color-text-status-warning, #855900)",
  red: "#e53935",
  grey: "var(--color-text-body-secondary, #5f6b7a)",
};

// `grandTotal` intentionally omitted: the Grand Total row has an
// empty `path`, so `formatPath` never dereferences that key.
const ROW_LEVEL_DISPLAY_LABEL: Record<
  Exclude<RowGroupLevel, "grandTotal">,
  string
> = {
  productLine: "Product Line",
  program: "Program",
  marketplace: "Marketplace",
  asinType: "ASIN Type",
};

const PIPELINE_DISPLAY_LABEL: Record<ValidationPipeline, string> = {
  LVS: "LVS",
  HATTRICK: "Hattrick",
};

const CELL_BASE_STYLE: React.CSSProperties = {
  boxSizing: "border-box",
  padding: "0.5rem 0.75rem",
  minHeight: "2rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  background: "transparent",
  fontWeight: 400,
  fontSize: "1.375rem",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  userSelect: "none",
};

const ACTIVE_RING_STYLE: React.CSSProperties = {
  boxShadow: "inset 0 0 0 2px var(--color-border-item-selected, #0972d3)",
  borderRadius: "4px",
};

function formatPath(path: RowGroupPath): string {
  if (path.length === 0) {
    return "";
  }
  const parts: string[] = [];
  for (const token of path) {
    const display = token.displayLabel ?? token.value;
    parts.push(`${ROW_LEVEL_DISPLAY_LABEL[token.level]}: ${display}`);
  }
  return parts.join(", ");
}

function buildAriaLabel(
  cell: PivotCellData,
  row: PivotRow,
  column: PivotColumn
): string {
  const stepDescription = `${PIPELINE_DISPLAY_LABEL[column.pipeline]} step ${
    column.stepName
  }`;
  const roundedPct = Math.round(cell.passRate);
  const counts = `${cell.passCount} of ${cell.totalCount} passing (${roundedPct}%)`;
  const pathLabel = formatPath(row.path);
  if (pathLabel === "") {
    return `${stepDescription} — ${counts}`;
  }
  return `${pathLabel} — ${stepDescription} — ${counts}`;
}

function buildTestId(row: PivotRow, column: PivotColumn): string {
  const pathKey = row.path.map((t) => `${t.level}:${t.value}`).join("/");
  const rowKey = pathKey === "" ? "grandTotal" : pathKey;
  return `pivot-cell-${rowKey}-${column.pipeline}-${column.stepName}`;
}

export const PivotCell: React.FC<PivotCellProps> = ({
  cell,
  row,
  column,
  onClick,
  isActiveFilter,
}) => {
  const testId = buildTestId(row, column);
  const textColor = TEXT_COLOR_BY_COLOR[cell.color];

  if (cell.totalCount === 0) {
    return (
      <div
        role="img"
        aria-disabled="true"
        tabIndex={-1}
        aria-label={buildAriaLabel(cell, row, column)}
        data-testid={testId}
        data-interactive="false"
        style={{
          ...CELL_BASE_STYLE,
          color: textColor,
          cursor: "default",
        }}
      >
        {cell.displayText}
      </div>
    );
  }

  const activate = () => {
    onClick({
      stepName: column.stepName,
      pipeline: column.pipeline,
      path: row.path,
    });
  };

  const style: React.CSSProperties = {
    ...CELL_BASE_STYLE,
    color: textColor,
    cursor: "pointer",
    ...(isActiveFilter ? ACTIVE_RING_STYLE : null),
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={buildAriaLabel(cell, row, column)}
      aria-pressed={isActiveFilter ? "true" : "false"}
      data-testid={testId}
      data-active={isActiveFilter ? "true" : undefined}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      }}
      style={style}
    >
      {cell.displayText}
    </div>
  );
};

export default PivotCell;
